import { TextInput, PhoneNumberInput } from "@business-finland/wif-ui-lib";
import { useCallback } from "react";

import useFormField from "../../../../hooks/useFormField";
import {
  companyEmailRegex,
  companyFormDefaultText,
  companyPhoneRegex,
} from "../constants";
import { useFormFieldChecks } from "../helpers";
import {
  CompanyFormState,
  companyFormActions,
  useCompanyFormDispatch,
  useCompanyFormState,
} from "../store";
import type { CompanyFormFieldProps } from "../types";

export function CompanyName({
  labelText = companyFormDefaultText.companyNameLabel,
  placeholderText = companyFormDefaultText.companyNamePlaceholder,
}: Partial<CompanyFormFieldProps>) {
  const { inputProps } = useContactFormField("companyName", true);

  return (
    <TextInput
      label={labelText}
      required
      placeholder={placeholderText}
      autoComplete="off"
      autoFocus
      {...inputProps}
    />
  );
}

export function CompanyPhone({
  labelText = companyFormDefaultText.companyPhoneLabel,
  placeholderText = companyFormDefaultText.companyPhonePlaceholder,
}: Partial<CompanyFormFieldProps>) {
  const { inputProps } = useContactFormField(
    "companyPhone",
    true,
    companyPhoneRegex
  );

  return (
    <PhoneNumberInput
      label={labelText}
      required
      placeholder={placeholderText}
      autoComplete="off"
      {...inputProps}
      height={"large"}
      value={undefined!}
    />
  );
}

export function CompanyEmail({
  labelText = companyFormDefaultText.companyEmailLabel,
  placeholderText = companyFormDefaultText.companyEmailPlaceholder,
}: Partial<CompanyFormFieldProps>) {
  const { inputProps } = useContactFormField(
    "companyEmail",
    true,
    companyEmailRegex
  );

  return (
    <TextInput
      label={labelText}
      required
      placeholder={placeholderText}
      autoComplete="off"
      {...inputProps}
      inputMode="email"
      type="email"
    />
  );
}

function useContactFormField(
  name: keyof CompanyFormState,
  required: boolean,
  formatRegex?: RegExp
) {
  const state = useCompanyFormState();
  const dispatch = useCompanyFormDispatch();
  const checks = useFormFieldChecks(required, formatRegex);
  const onError = useCallback(
    () => dispatch(companyFormActions.setValue({ [name]: "" })),
    [dispatch, name]
  );
  const onSuccess = useCallback(
    (val: string) => dispatch(companyFormActions.setValue({ [name]: val })),
    [dispatch, name]
  );

  return useFormField({
    name,
    onError,
    onSuccess,
    checks,
    defaultValue: state[name]?.toString(),
  });
}
